import React, {
  CSSProperties,
  forwardRef,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  autoUpdate,
  flip,
  FloatingList,
  Placement,
  Strategy,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import styled from 'styled-components';
import { floatingComponentStyles } from '../../../styles/shareableStyles';
import { IStyled } from '../../../styles/types';
import {
  ISingleSelectContext,
  SingleSelectContext,
} from './SingleSelectContext';
import { getScrollBarStyles } from '../../../config/styles';
import {
  FloatingLayerProvider,
  useFloatingLayerContext,
} from '../../FloatingManager';

interface IProps extends IStyled {
  trigger: ReactNode;
  children: ReactNode;
  menuMinWidth?: CSSProperties['minWidth'];
  menuMaxHeight?: CSSProperties['maxHeight'];
  placement?: Placement;
  strategy?: Strategy;
  disabled?: boolean;
  isDefaultOpen?: boolean;
  onOpen?: (event?: Event) => void;
  onClose?: (event?: Event) => void;
  onClick?: (event: SyntheticEvent) => void;
}

const Container = styled.div`
  flex: 0 0 auto;
  position: relative;
`;
const StyledMenu = styled.div`
  ${floatingComponentStyles};
  ${getScrollBarStyles(2)};
  overflow-y: auto;
  // By design, menu limit is 240px
  max-width: 240px;
`;

const SingleSelect = forwardRef<HTMLElement, IProps>(
  (
    {
      trigger,
      children,
      menuMinWidth,
      menuMaxHeight,
      placement,
      disabled,
      isDefaultOpen,
      onOpen,
      onClose,
      onClick,
      className,
      strategy,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(isDefaultOpen || false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const elementsRef = useRef<Array<HTMLElement | null>>([]);

    const handleSelect = useCallback(() => {
      setIsOpen(false);
      onClose?.();
    }, [onClose]);

    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange: (open, event) => {
        setIsOpen(open);
        if (open) {
          onOpen?.(event);
        } else {
          onClose?.(event);
        }
      },
      middleware: [flip()],
      whileElementsMounted: autoUpdate,
      placement: placement || 'bottom-end',
      strategy,
    });

    const listNav = useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      onNavigate: setActiveIndex,
      focusItemOnOpen: true,
    });

    const click = useClick(context, { enabled: !disabled });
    const dismiss = useDismiss(context, { bubbles: true });
    const role = useRole(context);

    const { getReferenceProps, getFloatingProps, getItemProps } =
      useInteractions([listNav, click, dismiss, role]);

    const selectContext = useMemo(
      (): ISingleSelectContext => ({
        getItemProps,
        handleSelect,
        activeIndex,
      }),
      [activeIndex, getItemProps, handleSelect],
    );

    const referenceElementRef = useMergeRefs([ref, refs.setReference]);
    const zIndex = useFloatingLayerContext();

    return (
      <FloatingLayerProvider>
        <Container className={className}>
          {React.isValidElement(trigger) &&
            React.cloneElement(trigger, {
              ...getReferenceProps({
                ref: referenceElementRef,
                ...trigger.props,
                onClick,
                'data-open': isOpen,
              }),
            })}
          <SingleSelectContext.Provider value={selectContext}>
            {isOpen && (
              <StyledMenu
                ref={refs.setFloating}
                style={{
                  ...floatingStyles,
                  zIndex,
                  minWidth: menuMinWidth,
                  maxHeight: menuMaxHeight || 'auto',
                }}
                {...getFloatingProps()}
              >
                <FloatingList elementsRef={elementsRef}>
                  {children}
                </FloatingList>
              </StyledMenu>
            )}
          </SingleSelectContext.Provider>
        </Container>
      </FloatingLayerProvider>
    );
  },
);

export default SingleSelect;
